import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import { AiOutlineEye } from "react-icons/ai";
import ExcelJS from "exceljs";
import { getSixthsenseRawCandidateDetails, base_URL } from "../../services/api";
import "../../styles/Candidates.scss";
import "../../styles/CandidateCreate.scss";

const ITEMS_PER_PAGE = 10;
const normalizeValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Not available";
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean).join(", ") || "Not available";
  }

  return String(value);
};

const parseSkills = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value)
    .split(/[,|/]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const mapCandidate = (item) => ({
  id: item?.id ?? item?.candidate_id ?? item?.candidateId ?? "",
  fullName: normalizeValue(item?.candidate_name || item?.full_name || item?.name || item?.candidateName),
  email: normalizeValue(item?.email_id || item?.email || item?.primary_email),
  secondaryEmail: normalizeValue(item?.secondary_email_id || item?.secondary_email || item?.alternate_email),
  mobile: normalizeValue(item?.mobile_number || item?.phone || item?.mobile),
  gender: normalizeValue(item?.gender),
  dob: normalizeValue(item?.dob),
  designation: normalizeValue(item?.designation || item?.title || item?.current_designation),
  company: normalizeValue(item?.company || item?.current_company),
  yearsOfExperience: normalizeValue(item?.years_of_exp || item?.experience || item?.yearsOfExperience),
  salary: normalizeValue(item?.salary_in_lakhs || item?.salary || item?.present_ctc),
  location: normalizeValue(item?.location || item?.current_location),
  preferredLocation: normalizeValue(item?.preferred_location || item?.preferredLocation),
  skills: parseSkills(item?.skills || item?.skill_set || item?.skills_list),
  education: normalizeValue(item?.education || item?.education_qualification || item?.qualification),
  resumeUrl: item?.resume_path
  ? `${process.env.REACT_APP_API_URL || base_URL}files/resume/${item.resume_path.replace(/\\/g, '/')}`
  : null,
});

const matchesGlobalSearch = (candidate, term) => {
  if (!term.trim()) return true;

  const query = term.trim().toLowerCase();

  const fields = [
    candidate.fullName,
    candidate.email,
    candidate.secondaryEmail,
    candidate.mobile,
    candidate.gender,
    candidate.dob,
    candidate.designation,
    candidate.company,
    candidate.yearsOfExperience,
    candidate.salary,
    candidate.location,
    candidate.preferredLocation,
    ...(candidate.skills || []),
    candidate.education,
  ];

  return fields.some(
    (field) => field && String(field).toLowerCase().includes(query)
  );
};
function SixthsenseRawCandidateDetails() {
  const [candidates, setCandidates] = useState([]);
  // const [searchName, setSearchName] = useState("");
  const [searchSkills, setSearchSkills] = useState([]);
  const [skillInputValue, setSkillInputValue] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const navigate = useNavigate();
  const { candidateId } = useParams();

  useEffect(() => {
    const fetchCandidates = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getSixthsenseRawCandidateDetails();
        if (response?.result_code === 200 && Array.isArray(response.result_info)) {
          const mapped = response.result_info.map(mapCandidate);
          setCandidates(mapped);
        } else {
          setCandidates([]);
          setError(response?.message || "Unable to fetch sixthsense raw candidates right now.");
        }
      } catch (err) {
        console.error("Error fetching sixthsense raw candidates:", err);
        setCandidates([]);
        setError("Failed to load sixthsense raw candidates. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [ searchSkills, globalSearch]);


  const filteredCandidates = candidates.filter((candidate) => {
    const skillMatch =
      searchSkills.length > 0
        ? searchSkills.some((skill) =>
            (candidate.skills || [])
              .map((entry) => (entry ? entry.toLowerCase() : ""))
              .includes(skill.toLowerCase())
          )
        : true;      
   const globalMatch = matchesGlobalSearch(candidate, globalSearch);

    return  skillMatch && globalMatch;
  });

  const pageCount = Math.ceil(filteredCandidates.length / ITEMS_PER_PAGE);
  const paginatedCandidates = filteredCandidates.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Candidates");

  worksheet.columns = [
  { header: "Name", key: "name" },
  { header: "Email", key: "email" },
  { header: "Secondary Email", key: "secondaryEmail" },
  { header: "Mobile", key: "mobile" },
  { header: "Gender", key: "gender" },
  { header: "DOB", key: "dob" },
  { header: "Designation", key: "designation" },
  { header: "Company", key: "company" },
  { header: "Years of Experience", key: "yearsOfExperience" },
  { header: "Salary (in lakhs)", key: "salary" },
  { header: "Location", key: "location" },
  { header: "Preferred Location", key: "preferredLocation" },
  { header: "Skills", key: "skills" },
  { header: "Education", key: "education" },
  {header: "Resume URL" , key:"resumeURL"},
];


filteredCandidates.forEach((item) => {
  worksheet.addRow({
    name: item.fullName || "-",
    email: item.email || "-",
    secondaryEmail: item.secondaryEmail || "-",
    mobile: item.mobile || "-",
    gender: item.gender || "-",
    dob: formatDate(item.dob) || "-",
    designation: item.designation || "-",
    company: item.company || "-",
    yearsOfExperience: item.yearsOfExperience || "-",
    salary: item.salary || "-",
    location: item.location || "-",
    preferredLocation: item.preferredLocation || "-",
    skills: item.skills.join(", ") || "-",
    education: item.education || "-",
    resume:item.resumeUrl || "-",
  });
});
  
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "SixthSense_Raw_CandidateDetails.xlsx";
    link.click();
    URL.revokeObjectURL(url);
  };

  const getPageNumbers = (currentPage, totalPages, maxPagesToShow = 5) => {
    const pages = [];
    const half = Math.floor(maxPagesToShow / 2);
    let start = Math.max(currentPage - half, 1);
    let end = start + maxPagesToShow - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxPagesToShow + 1, 1);
    }

    for (let index = start; index <= end; index += 1) {
      pages.push(index);
    }
    return pages;
  };
  return (
    <div className="requisitions-page">
      <div className="requisitions-header-row">
          <h1>SixthSense Raw Candidates</h1>
        <div className="export-btn" onClick={handleExport}>
          Export
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginBottom: "1.5rem",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
          <input
  type="text"
  placeholder="Global search (any field)"
  value={globalSearch}
  onChange={(e) => setGlobalSearch(e.target.value)}
  style={{
    minWidth: 220,
    maxWidth: 300,
    padding: "0.4rem .5rem",
    border: "1.5px solid #d1d5db",
    borderRadius: 6,
    background: "#fff",
    fontSize: "1rem",
  }}
  className="custom-placeholder"
  autoComplete="off"
/>

          <CreatableSelect
            isMulti
            options={Array.from(new Set(candidates.flatMap((candidate) => candidate.skills))).map((skill) => ({
              value: skill,
              label: skill,
            }))}
            value={searchSkills.map((skill) => ({ value: skill, label: skill }))}
            onChange={(selected) => {
              if (!selected) {
                setSearchSkills([]);
                return;
              }

              const uniqueSkills = [];
              selected.forEach((option) => {
                const exists = uniqueSkills.some(
                  (skill) => skill.toLowerCase() === option.value.toLowerCase()
                );
                if (!exists) {
                  uniqueSkills.push(option.value);
                }
              });
              setSearchSkills(uniqueSkills);
            }}
            inputValue={skillInputValue}
            onInputChange={(value, { action }) => {
              if (action === "input-change" || action === "set-value") {
                setSkillInputValue(value);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                event.stopPropagation();
                const normalized = (skillInputValue || "").trim();
                if (!normalized) return;
                const values = normalized.split(",").map((value) => value.trim()).filter(Boolean);
                setSearchSkills((prev) => {
                  const next = [...prev];
                  values.forEach((value) => {
                    const alreadyExists = next.some(
                      (skill) => skill.toLowerCase() === value.toLowerCase()
                    );
                    if (!alreadyExists) next.push(value);
                  });
                  return next;
                });
                setSkillInputValue("");
              }
            }}
            onCreateOption={(inputValue) => {
              const normalized = inputValue.trim();
              if (!normalized) return;
              const values = normalized.split(",").map((value) => value.trim()).filter(Boolean);
              setSearchSkills((prev) => {
                const next = [...prev];
                values.forEach((value) => {
                  const alreadyExists = next.some(
                    (skill) => skill.toLowerCase() === value.toLowerCase()
                  );
                  if (!alreadyExists) next.push(value);
                });
                return next;
              });
              setSkillInputValue("");
            }}
            placeholder="Search by skills"
            isSearchable={true}
            classNamePrefix="react-select-location"
            formatCreateLabel={(inputValue) => `Search for "${inputValue}"`}
            noOptionsMessage={() =>
              skillInputValue
                ? `Press Enter to search for "${skillInputValue}"`
                : "Type to search skills"
            }
            createOptionPosition="last"
            styles={{
              container: (base) => ({
                ...base,
                width: "340px",
                minWidth: "340px",
                maxWidth: "340px",
              }),
              control: (base) => ({
                ...base,
                minHeight: 38,
                maxHeight: 80,
                borderRadius: 6,
                fontSize: "1rem",
                background: "#fff",
                border: "1.5px solid #d1d5db",
                overflow: "hidden",
              }),
              valueContainer: (base) => ({
                ...base,
                maxHeight: 70,
                overflowY: "auto",
                overflowX: "hidden",
                flexWrap: "wrap",
                alignContent: "flex-start",
              }),
              multiValue: (base) => ({
                ...base,
                maxWidth: "120px",
                backgroundColor: "#e2e8f0",
              }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#f3f4f6" : state.isFocused ? "#f8fafc" : "#fff",
                color: "#111827",
              }),
            }}
          />
        </div>
      </div>
{loading ? (
  <div className="candidate-section">
    <div className="candidate-section-title">Loading sixthsense candidates...</div>
  </div>
) : error ? (
  <div className="candidate-section">
    <div className="candidate-section-title">Unable to load candidates</div>
    <p style={{ marginTop: "0.5rem", color: "#555" }}>{error}</p>
  </div>
) : (
  <>
    <div className="requisitions-table-wrapper" >
      <table className="requisitions-table candidates-table" >
        <thead>
          <tr>
            
            <th>Name</th>
            <th>Email</th>
            <th>Mobile</th>
            <th>Gender</th>
            <th style={{ minWidth: "180px" }}>DOB</th>
            <th style={{minWidth :"180px"}}>Designation</th>
            <th style={{minWidth :"180px"}}>Company</th>
            <th>Years of Experience</th>
            <th>Salary (in lakhs)</th>
            <th>Location</th>
            <th >Preferred Location</th>          
            <th>Skills</th>
            <th>Education</th>
            <th>Secondary Email Id</th>
            <th>Resume</th> 
          </tr>
        </thead>
        <tbody>
          {paginatedCandidates.length === 0 ? (
            <tr>
              <td colSpan={15} style={{ textAlign: "center", padding: "2rem 0" }}>
                No candidates found
              </td>
            </tr>
          ) : (
            paginatedCandidates.map((candidate) => (
              <tr key={candidate.id}>
                <td>{candidate.fullName}</td>
                <td>{candidate.email}</td>
                <td>{candidate.mobile}</td>
                <td>{candidate.gender}</td>
                <td>{formatDate(candidate.dob)}</td>
                <td title={candidate.designation || "-"}>
  <span
    style={{
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: 3,
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "300px", 
      lineHeight: "1.5em",
    }}
  >
    {candidate.designation || "-"}
  </span>
</td>
               
                <td>{candidate.company}</td>
                <td>{candidate.yearsOfExperience}</td>
                <td>{candidate.salary}</td>
                <td>{candidate.location}</td>
                <td >{candidate.preferredLocation}</td>
{/* <td style={{ maxWidth: '180px' }}>
  <div
    style={{
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      lineHeight: '1.5',
    }}
  >
    {candidate.preferredLocation
      ?.replace(/\\n/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .split(',')
      .join(',\u200B')
      .replace(/ /g, '\u00A0')}
  </div>
</td> */}

                <td title={candidate.skills.join(", ") || "-"}>
                  <span className="candidate-skills-column">
                    {candidate.skills.length > 0 ? candidate.skills.join(", ") : "-"}
                  </span>
                </td>
                <td  style={{
                        minWidth: "350px",
                        maxWidth: "350px",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                 }}>                  
                 {candidate.education}</td>
                <td>{candidate.secondaryEmail}</td>  
                <td>
                                    {candidate.resumeUrl ? (
                                      <a
                                        href={candidate.resumeUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="View Resume"
                                        // onClick={(e) => e.stopPropagation()}
                                      >
                                        <AiOutlineEye
                                          size={22}
                                          color="#0078b9"
                                          style={{
                                            verticalAlign: "middle",
                                          }}
                                        />
                                      </a>
                                    ) : (
                                      "-"
                                    )}
                               </td>            
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>    
          {pageCount > 1 && (
            <div className="settings-pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                &lt;
              </button>
              {getPageNumbers(page, pageCount, 5).map((pageNum) => (
                <button
                  key={pageNum}
                  className={"pagination-btn" + (page === pageNum ? " active" : "")}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              ))}
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pageCount}
              >
                &gt;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SixthsenseRawCandidateDetails;
